import React from 'react';
import { Dots } from './Dots';
import './TagCloud.scss';

export type Item<T> = T & {
    count: number;
    size?: 'xsmall' | 'small' | 'medium' | 'large';
};

export interface TagCloudProps<T> {
    items: Item<T>[];
    children: (item: Item<T>) => React.ReactElement;
}

function getMinAndMax<T>(items: Item<T>[]) {
    return items.reduce<{ min: number; max: number }>(
        (memo, item) => {
            const count = item.count;

            //calculate min
            if (count < memo['min']) {
                memo['min'] = count;
            }

            //calculate max
            if (count > memo['max']) {
                memo['max'] = count;
            }

            return memo;
        },
        { max: 0, min: 500000000 },
    );
}

/**
 * Dumb component logic to support returning items of differing sizes.
 *
 * @param props
 * @returns
 */
export function TagCloud<T>(props: TagCloudProps<T>) {
    const { min, max } = getMinAndMax(props.items);
    const bucketSize = (max - min) / 4;

    const xSmallUpperBound = min + bucketSize / 2;
    const smallUpperBound = xSmallUpperBound + bucketSize * 1.5;
    const mediumUpperBound = max - bucketSize * 1;
    const largeUpperBound = max;

    const getItemSize = (count: number) => {
        if (count <= xSmallUpperBound) return 'xsmall';
        if (count <= smallUpperBound) return 'small';
        if (count <= mediumUpperBound) return 'medium';
        if (count <= largeUpperBound) return 'large';
    };

    const midWayIndex = props.items.length / 2;
    const itemsHalfOne = props.items.slice(0, midWayIndex);
    const itemsHaflTwo = props.items.slice(midWayIndex);

    return (
        <div className="tag-cloud__container">
            <div className="tag-cloud__bubble-container">
                <div className="tag-cloud__bubbles">
                    {itemsHalfOne.map((item) =>
                        props.children({
                            ...item,
                            size: getItemSize(item.count),
                        }),
                    )}
                </div>
                <div className="tag-cloud__bubbles">
                    {itemsHaflTwo.map((item) =>
                        props.children({
                            ...item,
                            size: getItemSize(item.count),
                        }),
                    )}
                </div>
            </div>
            <div className="tag-cloud__image">
                <Dots />
            </div>
        </div>
    );
}

interface TagCloudBubbleProps {
    size: 'xsmall' | 'small' | 'medium' | 'large';
    [x: string]: unknown;
}

export const TagCloudBubble: React.FunctionComponent<TagCloudBubbleProps> = (
    props,
) => {
    return (
        <div className={`tag-cloud__bubble tag-cloud__bubble--${props.size}`}>
            <div className="tag-cloud__bubble--content">{props.children}</div>
        </div>
    );
};
